<script lang="ts" setup>
import { DeleteOutlined, EyeOutlined } from '@ant-design/icons-vue';
import { message } from '@rendora/common/composables/message';
import { postUserFeedBackInformation } from '@rendora/common/entities/user';
import { postYouyanUploadMediaPureUploadFile } from '@rendora/common/entities/youyan/upload_media';
import { getI18nService } from '@rendora/common/services/i18n';
import { getFeUrl } from '@rendora/common/utils/url';
import IconSuccess from '@rendora/design/assets/circle-success.svg?component';
import IconClose from '@rendora/design/assets/close.svg?component';
import IconPlus from '@rendora/design/assets/plus-large.svg?component';
import IconLoading from '@rendora/design/assets/spinner.svg?component';
import { Button } from '@rendora/design/components/button/index';
import { Img } from '@rendora/design/components/img/index';
import { Text } from '@rendora/design/components/text/index';
import type { FormInstance, UploadChangeParam } from 'ant-design-vue';
import {
  Form as AForm,
  FormItem as AFormItem,
  Input as AInput,
  Popover as APopover,
  Radio as ARadio,
  RadioGroup as ARadioGroup,
  Textarea as ATextarea,
  Upload as AUpload,
} from 'ant-design-vue';
import type { Rule } from 'ant-design-vue/es/form';
import type { UploadRequestOption as UploadRequestOptionType } from 'ant-design-vue/es/upload/interface';
import { computed, reactive, ref } from 'vue';
import { useRoute } from 'vue-router';

const { t: $t } = getI18nService();
const route = useRoute();

type FeedbackType = '0' | '1' | '2';

type UploadStatus = 'uploading' | 'done' | 'error';

interface UploadItem {
  uid: string;
  status: UploadStatus;
  url?: string;
}

interface ModelFormState {
  type: FeedbackType;
  description: string;
  phone: string;
  fileList: UploadItem[];
}

const formRef = ref<FormInstance>();
const visible = ref(true);
const loading = ref(false);

const modelForm = reactive<ModelFormState>({
  type: '0',
  description: '',
  phone: '',
  fileList: [],
});

// const modalInstance = inject<ModalInstance>('modalInstance');

const MAX_FILES = 4;
const dataCount = computed(() => `${modelForm.fileList.length}/${MAX_FILES}`);

const canUploadMoreFiles = computed(() => modelForm.fileList.length < MAX_FILES);

const maxLength = 300;

const getStringLength = (str: string): number => {
  let len = 0;
  const chineseRegex = /[\u4E00-\u9FA5]/g;
  const chinese = str?.match(chineseRegex);
  if (chinese) {
    len += chinese.length * 2;
  }

  return str?.length - (chinese?.length ?? 0) + len;
};

const textLength = computed(() => {
  const len = getStringLength(modelForm.description);
  return len > maxLength ? maxLength : len;
});

const sliceText = (text: string): string => {
  let length = 0;
  let result = '';
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const tempLen = getStringLength(char);
    if (length + tempLen <= maxLength) {
      result += char;
      length += tempLen;
    } else {
      break;
    }
  }
  return result;
};

const handleChange = (event: Event) => {
  const target = event.target as HTMLTextAreaElement | null;
  const text = target?.value ?? '';
  modelForm.description = sliceText(text);
};

const validate = (_rule: Rule, value: string): Promise<void> => {
  const emailReg =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  const telReg = /^(?:(?:\d{3}-)?\d{8}|^(?:\d{4}-)?\d{7,8})(?:-\d+)?$/;
  const mobReg = /^(13[0-9]|14[01456879]|15[0-35-9]|16[2567]|17[0-8]|18[0-9]|19[0-35-9])\d{8}$/;
  if (value) {
    const isOk = mobReg.test(value) || emailReg.test(value) || telReg.test(value);
    if (!isOk) {
      return Promise.reject(new Error($t('qing-shu-ru-zheng-que-de-lian-xi-fang-shi')));
    }
  }
  return Promise.resolve();
};

const updateUploadItem = (uid: string, patch: Partial<UploadItem>) => {
  const index = modelForm.fileList.findIndex(item => item.uid === uid);

  if (index === -1) {
    modelForm.fileList.push({
      uid,
      status: 'uploading',
      ...patch,
    });
    return;
  }

  modelForm.fileList[index] = {
    ...modelForm.fileList[index],
    ...patch,
  };
};

const beforeUpload = (file: File) => {
  const isFileMatch = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif'].includes(file.type);
  if (!isFileMatch) {
    message.error($t('wen-jian-lei-xing-bu-zheng-que'));
  }

  const isMatchSize = file.size / 1024 / 1024 < 3;
  if (!isMatchSize) {
    message.error($t('tu-pian-zui-da-wei-3mb'));
  }

  return isFileMatch && isMatchSize;
};

const handleDelete = (file: UploadItem) => {
  modelForm.fileList = modelForm.fileList.filter(item => item.uid !== file.uid);
};

const handleUploadChange = (info: UploadChangeParam) => {
  const { file } = info;
  const uid = file.uid;

  if (file.status === 'uploading') {
    updateUploadItem(uid, { status: 'uploading' });
  }

  if (file.status === 'removed') {
    modelForm.fileList = modelForm.fileList.filter(item => item.uid !== uid);
  }
};

const handleUpload = async (options: UploadRequestOptionType) => {
  const { file, onSuccess, onError } = options;
  const uid = file.uid;

  try {
    const formData = new FormData();
    formData.append('file', file as File);
    const response = await postYouyanUploadMediaPureUploadFile(formData);

    updateUploadItem(uid, {
      status: 'done',
      url: (response?.url as string | undefined) || '',
    });

    onSuccess?.(response);
  } catch (error) {
    updateUploadItem(uid, { status: 'error' });

    onError?.(error as Error);
  }
};

const handleSave = async () => {
  loading.value = true;
  try {
    await formRef.value?.validate();
    const response = await postUserFeedBackInformation({
      feedback_type: modelForm.type,
      feedback_source: route?.query?.feedback ? 1 : 0, // 编辑器1 服务平台0
      description: modelForm.description,
      screenshot_urls: modelForm.fileList.map(item => item.url || ''),
      content_information: modelForm.phone ?? '',
    });
    const res = response as { error_code?: number; error_msg?: string; message?: string };

    if (res.error_code === 0) {
      visible.value = false;
    } else {
      const errorMsg =
        res.error_msg || res.message || $t('cao-zuo-shi-bai-qing-shao-hou-zhong-shi');
      message.error(errorMsg);
    }
  } catch (_error) {
    // swallow error, message 已在接口层统一处理
  } finally {
    loading.value = false;
  }
};
</script>
<template>
  <template v-if="visible">
    <div class="feedback">
      <a-form ref="formRef" :hide-required-mark="true" :model="modelForm" layout="vertical">
        <a-form-item
          class="feedback__form-item-highlight"
          name="type"
          :rules="[{ required: true, message: $t('qing-xuan-ze-fan-kui-lei-xing') }]"
        >
          <a-radio-group v-model:value="modelForm.type">
            <a-radio value="0">
              <Text variant="14" color="t3">{{ $t('gong-neng-gu-zhang') }}</Text>
            </a-radio>
            <a-radio value="1">
              <Text variant="14" color="t3">{{ $t('chan-pin-jian-yi') }}</Text>
            </a-radio>
            <a-radio value="2">
              <Text variant="14" color="t3">{{ $t('qi-ta-wen-ti') }}</Text>
            </a-radio>
          </a-radio-group>
        </a-form-item>
        <div class="feedback__divide"></div>
        <a-form-item
          class="feedback__form-item-highlight"
          name="description"
          :rules="[
            {
              required: true,
              message: $t('qing-shu-ru-nin-de-wen-ti-huo-jian-yi'),
            },
          ]"
        >
          <template #label>
            <Text variant="14" color="t1" weight="medium">{{ $t('wen-ti-miao-shu') }}</Text>
          </template>
          <div class="feedback__question" :data-count="`${textLength} / ${maxLength}`">
            <a-textarea
              v-model:value="modelForm.description"
              :placeholder="$t('qing-shu-ru-nin-de-wen-ti-huo-jian-yi')"
              spellcheck="false"
              :autoSize="{ minRows: 4, maxRows: 6 }"
              @change="handleChange"
            >
            </a-textarea>
          </div>
        </a-form-item>
        <a-form-item
          class="feedback__form-item-highlight"
          name="screenshot"
          :rules="[{ required: false, message: $t('qing-shang-chuan-jie-tu') }]"
        >
          <template #label>
            <Text variant="14" color="t1" weight="medium">{{
              $t('shang-chuan-jie-tu-xuan-tian')
            }}</Text>
          </template>
          <div class="feedback__upload-tip">
            <span class="feedback__upload-tip-required-mark">*</span>
            <Text variant="12" color="t5">{{
              $t('jin-zhi-chi-shang-chuan-3m-nei-de-tu-pian')
            }}</Text>
          </div>
          <div class="feedback__upload-list" :data-count="dataCount">
            <template v-if="modelForm.fileList?.length">
              <div
                v-for="item of modelForm.fileList"
                :key="item?.uid"
                class="feedback__upload-list-item"
              >
                <Img
                  v-if="item?.status === 'done'"
                  :src="item?.url || ''"
                  alt=""
                  class="feedback__uploaded-image"
                />
                <div v-else class="feedback__upload-status">
                  <IconClose v-if="item?.status === 'error'" class="feedback__upload-error-icon" />
                  <IconLoading
                    v-else-if="item?.status === 'uploading'"
                    class="feedback__upload-loading-icon"
                  />
                </div>
                <div v-if="item?.status === 'done'" class="feedback__upload-action">
                  <div class="feedback__upload-action-option">
                    <a-popover>
                      <template #content>
                        <div class="feedback__preview">
                          <Img :src="item?.url || ''" alt="" class="feedback__preview-image" />
                        </div>
                      </template>
                      <EyeOutlined :title="$t('yu-lan')" />
                    </a-popover>
                    <DeleteOutlined :title="$t('shan-chu-wen-jian')" @click="handleDelete(item)" />
                  </div>
                </div>
              </div>
            </template>
            <a-upload
              v-if="canUploadMoreFiles"
              name="files"
              :max-count="MAX_FILES"
              accept=".jpg,.png,.jpeg,.gif"
              :beforeUpload="beforeUpload"
              :showUploadList="false"
              :custom-request="handleUpload"
              @change="handleUploadChange"
            >
              <div class="feedback__upload-list-item feedback__upload-list-item--add">
                <IconPlus class="feedback__upload-add-icon" />
              </div>
            </a-upload>
          </div>
        </a-form-item>
        <a-form-item
          class="feedback__form-item-highlight"
          name="phone"
          :rules="[{ required: false, validator: validate }]"
        >
          <template #label>
            <Text variant="14" color="t1" weight="medium">{{
              $t('lian-xi-fang-shi-xuan-tian')
            }}</Text>
          </template>
          <a-input
            v-model:value="modelForm.phone"
            :placeholder="$t('qing-liu-xia-nin-de-lian-xi-fang-shi-yi-bian-fan-kui-jie-guo')"
            class="feedback__contact-input"
            :maxlength="64"
          ></a-input>
        </a-form-item>
      </a-form>
    </div>
    <div class="feedback__submit-divider"></div>
    <div class="feedback__submit">
      <Button
        variant="purple"
        size="default"
        class="feedback__submit-button btn-full-width"
        :loading="loading"
        @click="handleSave"
      >
        {{ $t('ti-jiao') }}
      </Button>
    </div>
  </template>
  <div v-else class="feedback__result">
    <div class="feedback__result-content">
      <div class="feedback__result-success-icon-wrapper">
        <IconSuccess class="feedback__result-success-icon" />
      </div>
      <Text variant="18" color="t1" weight="medium" class="feedback__result-success-title">
        {{ $t('gan-xie-nin-de-fan-kui') }}
      </Text>
      <div class="feedback__result-qrcode">
        <Img
          class="feedback__result-qrcode-image"
          :src="getFeUrl('/youyan-platform/png/talk-team-qrcode.png')"
          alt="qrcode"
          draggable="false"
        />
      </div>
      <div class="feedback__result-info">
        <Text variant="14" color="t5" class="feedback__result-info-line">{{
          $t('jia-ru-chan-pin-tan-lun-zu')
        }}</Text>
        <Text variant="14" color="t5">{{ $t('zhui-zong-wen-ti-zui-xin-jin-zhan') }}</Text>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.feedback__result-success-icon-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
}

// 变量定义 - 调整为更紧凑的间距
$spacing-xs: 4px;
$spacing-sm: 8px;
$spacing-md: 12px;
$spacing-lg: 16px;
$spacing-xl: 20px;
$spacing-2xl: 24px;
$spacing-3xl: 32px;

$border-radius-sm: 6px;
$border-radius-md: 8px;
$border-radius-lg: 12px;

$transition-base: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
$transition-slow: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

.feedback {
  padding: $spacing-md $spacing-lg 0;

  // 响应式设计
  @media (max-width: 768px) {
    padding: $spacing-sm $spacing-md 0;
  }

  // 表单项样式优化
  .feedback__form-item-highlight {
    margin-bottom: $spacing-md;

    :deep(.ant-form-item-label > label) {
      margin-bottom: $spacing-xs;

      &::before {
        display: none;
      }
    }

    :deep(.ant-form-item-explain-error) {
      margin-top: $spacing-xs;
      font-size: var(--x-text-12-size);
    }
  }

  // 文本域样式优化
  :deep(textarea.ant-input) {
    padding: $spacing-sm;
    border-radius: $border-radius-md;
    border: 1px solid var(--x-back-4);
    transition: $transition-base;
    font-size: var(--x-text-14-size);
    line-height: var(--x-text-14-line-height);
    resize: vertical;

    &:focus,
    &:hover {
      border-color: var(--x-purple-6);
      box-shadow: var(--x-shadow);
    }
  }

  // 单选框组样式优化
  :deep(.ant-radio-group) {
    display: flex;
    flex-wrap: wrap;
    gap: $spacing-sm;
    width: 100%;
  }

  :deep(.ant-radio-wrapper) {
    background-color: var(--x-back-2);
    border-radius: $border-radius-md;
    padding: $spacing-sm $spacing-md $spacing-sm $spacing-sm;
    margin-inline: 0;
    border: 1px solid transparent;
    transition: $transition-base;
    flex: 1;
    min-width: 100px;
    cursor: pointer;

    &:hover {
      background-color: var(--x-back-3);
      border-color: var(--x-purple-3);
    }

    &.ant-radio-wrapper-checked {
      background-color: var(--x-purple-1);
      border-color: var(--x-purple-6);
      color: var(--x-purple-6);
    }

    .ant-radio {
      margin-right: $spacing-sm;
    }
  }

  // 分割线样式优化
  .feedback__divide {
    background: linear-gradient(90deg, transparent, var(--x-back-4), transparent);
    height: 1px;
    margin: $spacing-md 0;
    border: none;
  }

  // 字符计数样式优化
  .feedback__question {
    position: relative;

    &::after {
      position: absolute;
      right: $spacing-sm;
      bottom: $spacing-sm;
      font-size: var(--x-text-12-size);
      color: var(--x-text-5);
      font-weight: var(--x-weight-regular);
      content: attr(data-count);
      background: rgba(255, 255, 255, 0.9);
      padding: 2px 6px;
      border-radius: $border-radius-sm;
      backdrop-filter: blur(4px);
    }
  }

  // 上传提示样式优化
  .feedback__upload-tip {
    margin-bottom: $spacing-sm;
    display: flex;
    align-items: center;
    gap: $spacing-xs;

    .feedback__upload-tip-required-mark {
      color: var(--x-error-6);
      font-weight: 500;
    }
  }

  // 上传列表样式优化
  .feedback__upload-list {
    display: flex;
    flex-wrap: wrap;
    gap: $spacing-sm;
    position: relative;

    &::after {
      position: absolute;
      bottom: -$spacing-lg;
      right: 0;
      font-size: var(--x-text-12-size);
      color: var(--x-text-6);
      content: attr(data-count);
    }

    // 重置 Ant Design Upload 组件默认样式
    :deep(.ant-upload) {
      display: block;
      width: auto;
      height: auto;
      background: transparent;
      border: none;
      border-radius: 0;
    }

    :deep(.ant-upload-select) {
      display: block;
      width: auto;
      height: auto;
      background: transparent !important;
      border: none !important;
      border-radius: 0;
    }

    .feedback__upload-list-item {
      position: relative;
      width: 80px;
      height: 80px;
      background-color: var(--x-back-4);
      border-radius: $border-radius-md;
      transition: $transition-slow;
      overflow: hidden;
      box-shadow: var(--x-shadow);

      &.feedback__upload-list-item--add {
        background-color: var(--x-back-2);
        display: flex;
        justify-content: center;
        align-items: center;
        color: var(--x-purple-6);
        cursor: pointer;
        border: 2px dashed var(--x-back-4);
        transition: $transition-base;

        &:hover {
          border-color: var(--x-purple-6);
          background-color: var(--x-purple-1);
          transform: translateY(-2px);
          box-shadow: var(--x-shadow);
        }

        &:active {
          transform: translateY(0);
        }
      }

      .feedback__uploaded-image {
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: $border-radius-md;
      }

      .feedback__upload-status {
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        background-color: var(--x-back-3);

        .feedback__upload-error-icon {
          width: 28px;
          height: 28px;
          color: var(--x-error-6);
        }

        .feedback__upload-loading-icon {
          width: 28px;
          height: 28px;
          color: var(--x-purple-6);
          animation: spin 1s linear infinite;
        }
      }

      .feedback__upload-add-icon {
        width: 28px;
        height: 28px;
        transition: $transition-base;
      }

      .feedback__upload-action {
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
        display: flex;
        justify-content: center;
        align-items: center;
        border-radius: $border-radius-md;
        background: rgba(0, 0, 0, 0.4);
        backdrop-filter: blur(4px);
        opacity: 0;
        transition: $transition-base;

        .feedback__upload-action-option {
          display: flex;
          gap: $spacing-sm;
          color: var(--x-back-1);
          font-size: var(--x-text-16-size);

          .anticon {
            padding: $spacing-xs;
            border-radius: $border-radius-sm;
            transition: $transition-base;
            cursor: pointer;

            &:hover {
              background-color: rgba(255, 255, 255, 0.2);
              transform: scale(1.1);
            }
          }
        }

        &:hover {
          opacity: 1;
        }
      }
    }
  }

  // 预览容器样式优化
  .feedback__preview {
    width: 300px;
    height: 300px;
    padding: $spacing-sm;
    background: white;
    border-radius: $border-radius-md;
    box-shadow: var(--x-shadow);

    .feedback__preview-image {
      width: 100%;
      height: 100%;
      border-radius: $border-radius-sm;
      object-fit: contain;
    }
  }

  // 联系方式输入框样式优化
  .feedback__contact-input {
    max-width: 280px;

    :deep(.ant-input) {
      border-radius: $border-radius-md;
      transition: $transition-base;

      &:focus,
      &:hover {
        border-color: var(--primary);
        box-shadow: var(--x-shadow);
      }
    }
  }
}

// 提交区域样式优化
.feedback__submit-divider {
  margin-top: $spacing-xl;
  border-top: 1px solid var(--x-back-4);
  background: linear-gradient(90deg, transparent, var(--x-back-4), transparent);
}

.feedback__submit {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: $spacing-md 0;

  .feedback__submit-button {
    min-width: 180px;
    max-width: 320px;
    box-shadow: var(--x-shadow);

    &:hover {
      transform: translateY(-1px);
      box-shadow: var(--x-shadow);
    }

    &:active {
      transform: translateY(0);
    }
  }
}

// 成功页面样式优化
.feedback__result {
  width: 100%;
  min-height: 400px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: $spacing-xl $spacing-md;

  .feedback__result-content {
    text-align: center;
    max-width: 400px;

    .feedback__result-success-icon {
      width: 64px;
      height: 64px;
      color: var(--x-success-6);
      margin-bottom: $spacing-md;
      animation: successPulse 0.6s ease-out;
    }

    .feedback__result-success-title {
      margin-bottom: $spacing-xl;
    }

    .feedback__result-qrcode {
      width: 140px;
      height: 140px;
      margin: 0 auto $spacing-lg;
      padding: $spacing-sm;
      background: white;
      border-radius: $border-radius-lg;
      box-shadow: var(--x-shadow);

      .feedback__result-qrcode-image {
        width: 100%;
        height: 100%;
        border-radius: $border-radius-md;
      }
    }

    .feedback__result-info {
      .feedback__result-info-line {
        display: block;
        margin-bottom: $spacing-xs;
      }
    }
  }
}

// 动画定义
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes successPulse {
  0% {
    transform: scale(0.8);
    opacity: 0;
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

// 响应式优化
@media (max-width: 768px) {
  .feedback {
    :deep(.ant-radio-group) {
      flex-direction: column;
      gap: $spacing-sm;
    }

    :deep(.ant-radio-wrapper) {
      min-width: auto;
      width: 100%;
    }

    .feedback__upload-list {
      .feedback__upload-list-item {
        width: 80px;
        height: 80px;
      }
    }

    .contact-input {
      max-width: 100%;
    }
  }

  .feedback__submit {
    .feedback__submit-button {
      width: 100%;
      max-width: none;
    }
  }

  .feedback__result {
    .feedback__result-content {
      .feedback__result-qrcode {
        width: 120px;
        height: 120px;
      }
    }
  }
}

// 深色模式适配
@media (prefers-color-scheme: dark) {
  .feedback {
    :deep(textarea.ant-input) {
      background-color: var(--x-back-1);
      color: var(--x-text-1);
    }

    .feedback__preview {
      background: var(--x-back-1);
    }
  }
}
</style>
